import { type Project, useStores } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { MainLayoutHeaderTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import { Composer } from '@/components/blocks/Thread/Composer';
import { XStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { EntityIconChip, IconPicker } from '@/components/ui/icon-picker';
import { RenameField } from '@/components/ui/RenameField/RenameField';
import { Page } from '@/design-system-v2/page';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { meetingApis } from '@/features/meetings/meetings.query';
import { meetingStore } from '@/features/meetings/meetings.state';
import {
  ProjectShareButton,
  ProjectShareCard,
  ProjectVisibilityBadge,
} from '@/features/projects/project-share';
import { uiStore } from '@/stores/ui.store';
import { inBrowser } from '@/utils';
import { trackEvent } from '@/utils/analytics';
import { NEW_CHAT_ID } from '@/utils/constants';

import { AddExistingItemsButton } from './add-existing-items-modal';
import { useProjectActions } from './project-actions';
import { ShareProjectModal } from './share-project-modal';

interface ProjectHeaderProps {
  project: Project;
  hideAddExisting?: boolean;
}

export const ProjectHeader = observer(function ProjectHeader({
  project,
  hideAddExisting,
}: ProjectHeaderProps) {
  const navigate = useNavigate();
  const { projects } = useStores();
  const { renameProject, setProjectAssignment, setProjectIcon } =
    useProjectActions();
  const [shareOpen, setShareOpen] = useState(false);

  const isMeetingRecording = meetingStore.recording.status === 'recording';

  const handleStartMeeting = () => {
    if (inBrowser()) {
      navigate({ to: '/meetings' });
      return;
    }

    meetingApis.enqueueStartMeeting({
      payload: { startReason: 'manual', projectId: project.id },
    });
  };

  const handleToggleFavorite = () => {
    void projects.updateProject(project, { starred: !project.starred });
    trackEvent('PROJECT_STARRED', {
      favorite: !project.starred,
      origin: 'project_header',
    });
  };

  const title = project.capabilities.canRename ? (
    <RenameField
      value={project.name}
      onRename={(name) => void renameProject(project.id, name)}
      editOnClick
      autoResize
    />
  ) : (
    project.name
  );

  const meetingButton = (
    <Button
      size="sm"
      variant={'secondary'}
      className="gap-1.5"
      onClick={handleStartMeeting}
      disabled={isMeetingRecording}
      tooltip={
        isMeetingRecording ? 'A meeting is already recording' : undefined
      }
    >
      <Icons.dictation className="size-3.5" />
      New meeting
    </Button>
  );

  const composer = (
    <Composer
      isNewThread
      threadId={NEW_CHAT_ID}
      projectId={project.id}
      hideProjectSelector
      setIsDraggingFile={(value) => uiStore.setIsDraggingFile(value)}
      onThreadCreated={(threadId) => {
        void setProjectAssignment({ kind: 'thread', id: threadId }, project.id);
      }}
    />
  );

  const shareModal = (
    <ShareProjectModal
      project={project}
      open={shareOpen}
      onOpenChange={setShareOpen}
    />
  );

  return (
    <>
      <MainLayoutHeaderTunnel.In>
        <XStack className="min-w-0 items-center gap-2.5">
          <IconPicker
            value={project.icon}
            onChange={(projectIcon) =>
              void setProjectIcon(project, projectIcon)
            }
          >
            <button
              type="button"
              aria-label="Change project icon"
              className="inline-flex shrink-0 items-center justify-center rounded-md transition-opacity hover:opacity-80 focus-ring"
            >
              <EntityIconChip icon={project.icon} size={14} />
            </button>
          </IconPicker>
          <Page.Title>{title}</Page.Title>
          <ProjectVisibilityBadge project={project} />
        </XStack>
      </MainLayoutHeaderTunnel.In>
      <Page.HeaderActions>
        <XStack className="items-center gap-2">
          <RoundIconButton
            size="sm"
            icon={project.starred ? Icons.starFilled : Icons.star}
            label={project.starred ? 'Unfavorite project' : 'Favorite project'}
            onClick={handleToggleFavorite}
          />
          <ProjectShareButton
            project={project}
            onOpen={() => setShareOpen(true)}
          />
          {!hideAddExisting && (
            <AddExistingItemsButton
              projectId={project.id}
              variant="secondary"
            />
          )}
          {meetingButton}
        </XStack>
      </Page.HeaderActions>
      <ProjectShareCard
        project={project}
        onOpenShare={() => setShareOpen(true)}
      />
      {composer}
      {shareModal}
    </>
  );
});
